import type { IconName } from "swing-components";

import type { AppearsWhen, QuestionStatus, Step, StepComputed } from "~onboarding/utils";

/**
 * Task type that determines behavior and display options.
 *
 * - "REQUIRED": Task must be completed to progress (default behavior)
 * - "OPTIONAL": Task is optional and displays an "Optional" label
 * - "OPTIONAL_NO_LABEL": Task is optional but does NOT display an "Optional" label
 */
export type TaskType = "REQUIRED" | "OPTIONAL" | "OPTIONAL_NO_LABEL";

/**
 * A Task has one or more Steps.
 *
 * This type will be defined by Product.
 */
export type Task = {
  name: string; // e.g. "About Swing"
  steps: Step[];
  /**
   * Name of the icon for this task
   */
  icon: Extract<
    IconName,
    | "Handshake"
    | "Rocket Launch"
    | "Apple"
    | "Signature"
    | "Fingerprint"
    | "Policy"
    | "Health"
    | "Briefcase"
    | "Payments"
    | "Shield"
    | "Person"
    | "Person Add"
    | "License"
    | "GraduationCap"
  >;
  /**
   * Content appearing under the name on the TaskCard used to describe how long
   * this Task will take to complete.
   *
   * @example: "5 min"
   */
  durationTimeText: string;
  /**
   * Type of task that determines behavior and display options.
   *
   * - "REQUIRED": Task must be completed to progress (default behavior)
   * - "OPTIONAL": Task is optional and displays an "Optional" label
   * - "OPTIONAL_NO_LABEL": Task is optional but does NOT display an "Optional" label
   *
   * @default "REQUIRED" (when undefined)
   */
  type?: TaskType;
  /**
   * @deprecated Use `type: "OPTIONAL"` or `type: "OPTIONAL_NO_LABEL"` instead.
   *
   * [Optional] Designate a Task as optional which will not block a Group from
   * being completed if this Task is not completed.
   *
   * NOTE: That if this Task is not completed but the Group that it's in is
   * completed, then it will be placed into the next incomplete Group until the
   * last Group.
   *
   * @example:
   * // Given a Group with 2 Tasks, one optional and one required.
   * const group1 = {
   *   name: "Group 1",
   *   tasks: [OptionalTask, RequiredTask1],
   * }
   * // And another Group with 1 Task
   * const group2 = {
   *   name: "Group 2",
   *   tasks: [RequiredTask2],
   * }
   * // When the Optional Task is completed, and the Required Task1 is completed
   * // Then expect the Group 1 to be completed and the Group 2 to include the
   * // Optional Task as the end of the Group 2's Task list.
   * console.log(group2.tasks); // [RequiredTask2, OptionalTask]
   */
  isOptional?: boolean;
  /**
   * Controls when the Task should appear based on user attributes.
   *
   * @default undefined - Task always appears
   *
   * When defined, Task will only appear when ALL specified criteria are met.
   * Task will be hidden if any required attribute is missing or mismatched.
   *
   * Task Logic:
   * - Will NOT BE visible when there IS NO matching onboarding attribute
   * - Will BE visible when there IS a matching onboarding attribute AND
   *   matching value
   * - Will NOT BE visible when there IS a matching onboarding attribute AND
   *   NO matching value
   *
   * @example
   * // Task only shows when user's state is explicitly "california"
   * const californiaTask = {
   *   name: "California Specific Task",
   *   appearsWhen: {
   *     "state": ["california"]
   *   }
   * }
   *
   * @example
   * // Task shows only when user selected "teaching" OR "administration" for role
   * const educatorTask = {
   *   name: "Educator Onboarding",
   *   appearsWhen: {
   *     "role": ["teaching", "administration"]
   *   }
   * }
   */
  appearsWhen?: AppearsWhen;
};

/**
 * A Task with computed properties.
 *
 * This type will be computed from the Sub's attributes.
 */
export type TaskComputed = Omit<Task, "steps"> & {
  /**
   * Steps with computed properties.
   */
  steps: StepComputed[];
  /**
   * A value between 0 and 1 representing the progress of the Task.
   *
   * NOTE: Progress is calculated: `# of Questions with status === "COMPLETED"` / Total Questions
   *
   * Why: This is computed since the LandingPage TaskCard must also know the
   * progress of the Task.
   */
  progress: number; // e.g. 0.5
  /**
   * The status of a Task.
   *
   * States:
   * - `undefined`: When all Steps do not have a single Questions answered.
   * - `AWAITING_RESULTS`: When any Step has has a `status` of `AWAITING_RESULTS`
   * - `UNDER_REVIEW`: When any Step has has a `status` of `UNDER_REVIEW`
   * - `ACTION_REQUIRED`: When any Step has has a `status` of `ACTION_REQUIRED`
   * - `COMPLETED`: When all Steps have a `status` of `COMPLETED`
   *
   * Why: This is required to help the Group component compute it's `status` as
   * well as help the Task component understand if it's in a read-only state.
   */
  status?: QuestionStatus;

  /**
   * Once all Steps for a Task have an updatedAt field the Task's updatedAt will
   * be the most recent updatedAt of the Steps. This signifies when the Task
   * was completed or the latest time when it was under some sort of review.
   */
  updatedAt?: Date;
};

/********** Helper Functions **********/

/**
 * Determines if a task is optional (doesn't block group progression).
 *
 * A task is considered optional if:
 * - `type` is "OPTIONAL" or "OPTIONAL_NO_LABEL", OR
 * - `isOptional` is true (deprecated, for backward compatibility)
 *
 * Note: When `type` is explicitly set, it takes precedence over `isOptional`.
 *
 * @param task - The task to check (Task or TaskComputed)
 * @returns true if the task is optional, false otherwise
 */
export function isTaskOptional(task: Pick<Task, "type" | "isOptional">): boolean {
  // When `type` is explicitly set, it takes precedence over `isOptional`
  if (task.type === "OPTIONAL" || task.type === "OPTIONAL_NO_LABEL") {
    return true;
  }
  if (task.type === "REQUIRED") {
    return false;
  }
  // Fall back to deprecated `isOptional` for backward compatibility (only when `type` is undefined)
  return task.isOptional === true;
}

/**
 * Determines if the "Optional" label should be displayed for a task.
 *
 * The label should be shown if:
 * - `type` is "OPTIONAL", OR
 * - `isOptional` is true AND `type` is not "OPTIONAL_NO_LABEL"
 *
 * @param task - The task to check (Task or TaskComputed)
 * @returns true if the "Optional" label should be shown, false otherwise
 */
export function shouldShowOptionalLabel(task: Pick<Task, "type" | "isOptional">): boolean {
  // Explicitly hide label for OPTIONAL_NO_LABEL type
  if (task.type === "OPTIONAL_NO_LABEL") {
    return false;
  }
  // Show label for OPTIONAL type
  if (task.type === "OPTIONAL") {
    return true;
  }
  // Fall back to deprecated `isOptional` for backward compatibility
  return task.isOptional === true;
}
